import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { calculateDistanceToSegment, calculateDistance } from './routeHelpers';
import { logError, logInfo } from '../../../utils/logger';

export const loadRouteData = async (surveyId) => {
  // Initialize arrays before the try block to prevent access before initialization
  let routes = [];
  let processedPois = [];
  let allPois = [];

  try {
    // Get all POIs for the survey first
    const poisRef = collection(db, 'pois');
    const poisQuery = query(
      poisRef,
      where('surveyId', '==', surveyId)
    );
    const poisSnapshot = await getDocs(poisQuery);
    allPois = poisSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get routes
    const routesRef = collection(db, 'routes');
    const routesQuery = query(
      routesRef,
      where('surveyId', '==', surveyId),
      orderBy('createdAt', 'asc')
    );
    const routesSnapshot = await getDocs(routesQuery);
    
    // Process routes and associate POIs
    routes = routesSnapshot.docs.map(doc => {
      const route = { id: doc.id, ...doc.data() };
      
      // Find POIs for this route
      const routePois = allPois.filter(poi => {
        // Skip if already processed
        if (processedPois.includes(poi.id)) return false;
        
        // If no routeId, check proximity to route
        if (route.trackPoints?.length) {
          let minDistance = Infinity;
          route.trackPoints.forEach((point, index) => {
            const nextPoint = route.trackPoints[index + 1];
            if (!nextPoint) return;
            
            const distance = calculateDistanceToSegment(
              poi.coordinates,
              point,
              nextPoint
            );
            minDistance = Math.min(minDistance, distance);
          });
          return minDistance <= 0.1; // Within 100m of route
        }
        return false;
      });

      // Calculate distance from start for each POI
      const poisWithDistance = routePois.map(poi => {
        // Mark POI as processed
        processedPois.push(poi.id);
        
        let distanceFromStart = 0;
        if (route.trackPoints?.length) {
          // Find closest point on route
          let minDistance = Infinity;
          let closestIndex = 0;
          
          route.trackPoints.forEach((point, index) => {
            const distance = calculateDistance(
              { latitude: point.latitude, longitude: point.longitude },
              poi.coordinates
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestIndex = index;
            }
          });
          
          // Calculate distance to this point
          for (let i = 0; i < closestIndex; i++) {
            distanceFromStart += calculateDistance(
              route.trackPoints[i],
              route.trackPoints[i + 1]
            );
          }
        }
        
        return {
          ...poi,
          distanceFromStart,
          routeId: route.id // Associate POI with this route
        };
      });
      
      // Add processed POIs to the final list
      processedPois = [...processedPois, ...poisWithDistance];
      
      return {
        ...route,
        poiCount: poisWithDistance.length
      };
    });

    // Sort all processed POIs by distance from start
    const sortedPois = processedPois
      .sort((a, b) => a.distanceFromStart - b.distanceFromStart);

    logInfo(`Loaded ${routes.length} routes and ${sortedPois.length} POIs for survey ${surveyId}`);

    return {
      routes,
      pois: sortedPois
    };
  } catch (error) {
    logError('Failed to load route data:', error);
    throw new Error(`Failed to load route data: ${error.message}`);
  }
};